import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Peer from "simple-peer";
import { useDispatch, useSelector } from "react-redux";
import useSocket from "./useSocket";
import useSendMessage from "./useSendMessage";
import { modalActions } from "../store/modalSlice";
import useCounter from "./useCounter";

const usePeer = ({ mediaOptions, callDetail }) => {
  const userMediaRef = useRef(null);
  const partnerMediaRef = useRef(null);
  const peerRef = useRef(null);

  const dispatch = useDispatch();
  const { socketEmit, socketListen, socket, userId } = useSocket();
  const { sendMessage } = useSendMessage();

  const currentChatRoomId = useSelector(
    (state) => state.chatReducer.currentChatRoom?._id
  );

  const [userStream, setUserStream] = useState(null);
  const [callStatus, setCallStatus] = useState("calling");
  const [callAccepted, setCallAccepted] = useState(false);

  const { formattedTime: duration, startCounter, stopCounter } = useCounter({
    showCentiseconds: false,
  });

  // ✅ Memoize iceConfig to avoid re-creation
  const iceConfig = useMemo(
    () => ({
      iceServers: [
        {
          urls: "turn:57.180.200.107:3478?transport=udp",
          username: "myusername",
          credential: "0x8fcc8ae2c169463bab76eb7124aede7b",
        },
        {
          urls: "turn:57.180.200.107:3478?transport=tcp",
          username: "myusername",
          credential: "0x8fcc8ae2c169463bab76eb7124aede7b",
        },
        {
          urls: "turns:57.180.200.107:5349",
          username: "myusername",
          credential: "0x8fcc8ae2c169463bab76eb7124aede7b",
        },
      ],
      iceTransportPolicy: "relay",
    }),
    []
  );

  // ✅ Get user media (run only once unless mediaOptions changes)
  useEffect(() => {
    let active = true;
    navigator.mediaDevices
      .getUserMedia(mediaOptions)
      .then((stream) => {
        if (!active) return;
        setUserStream(stream);
        if (userMediaRef.current) userMediaRef.current.srcObject = stream;
      })
      .catch((err) => console.error("Media access error:", err));

    return () => {
      active = false;
    };
  }, [mediaOptions]);

  // ✅ Cleanup helper
  const cleanup = useCallback(
    (peerInstance = peerRef.current) => {
      if (peerInstance) {
        peerInstance.destroy();
        peerRef.current = null;
      }
      if (userStream) {
        userStream.getTracks().forEach((t) => t.stop());
      }
      stopCounter();
    },
    [userStream, stopCounter]
  );

  // ✅ Caller setup
  useEffect(() => {
    if (!userStream || !callDetail?.caller) return;
    if (peerRef.current) return; // avoid duplicate peer

    const peer = new Peer({
      initiator: true,
      trickle: true,
      stream: userStream,
      config: iceConfig,
    });
    peerRef.current = peer;

    peer.on("signal", (signalData) => {
      socketEmit(
        "user:callRequest",
        {
          signalData,
          callType: mediaOptions.video ? "video" : "voice",
          chatRoomId: currentChatRoomId,
          userId,
        },
        (ack) => {
          setCallStatus(ack ? "ringing" : "calling");
          setTimeout(() => {
            if (!callAccepted) {
              dispatch(modalActions.closeModal());
              denyCall("Missed");
            }
          }, 60000);
        }
      );
    });

    peer.on("stream", (stream) => {
      if (partnerMediaRef.current) partnerMediaRef.current.srcObject = stream;
    });

    const onCallAccepted = ({ signalData }) => {
      peer.signal(signalData);
      setCallAccepted(true);
      startCounter();
    };

    const onEndCall = () => cleanup(peer);

    socketListen("user:callAccepted", onCallAccepted);
    socketListen("user:endCall", onEndCall);

    peer.on("close", () => cleanup(peer));

    return () => {
      socket.off("user:callAccepted", onCallAccepted);
      socket.off("user:endCall", onEndCall);
      cleanup(peer);
    };
  }, [
    userStream,
    callDetail?.caller,
    socketEmit,
    socketListen,
    userId,
    currentChatRoomId,
    dispatch,
    callAccepted,
    iceConfig,
    startCounter,
    cleanup,
  ]);

  // ✅ Receiver setup
  useEffect(() => {
    if (!userStream || !callAccepted || callDetail?.caller) return;
    if (peerRef.current) return;

    const peer = new Peer({
      initiator: false,
      trickle: true,
      stream: userStream,
      config: iceConfig,
    });
    peerRef.current = peer;

    peer.on("signal", (signalData) => {
      socketEmit("user:callAccepted", {
        signalData,
        chatRoomId: callDetail.chatRoomId,
      });
      startCounter();
    });

    peer.signal(callDetail.callerSignal);

    peer.on("stream", (stream) => {
      if (partnerMediaRef.current) partnerMediaRef.current.srcObject = stream;
    });

    peer.on("close", () => cleanup(peer));

    return () => cleanup(peer);
  }, [
    callAccepted,
    userStream,
    callDetail?.caller,
    callDetail?.callerSignal,
    callDetail?.chatRoomId,
    socketEmit,
    startCounter,
    cleanup,
    iceConfig,
  ]);

  // ✅ Call denied listener
  useEffect(() => {
    const onCallDenied = () => {
      setCallStatus("denied");
      dispatch(modalActions.closeModal());
      cleanup();
    };

    socketListen("user:callDenied", onCallDenied);
    return () => socket.off("user:callDenied", onCallDenied);
  }, [socketListen, dispatch, cleanup]);

  // ✅ Accept, deny, end call handlers
  const acceptCall = useCallback(() => setCallAccepted(true), []);

  const denyCall = useCallback(
    (reason) => {
      socketEmit("user:callDenied", {
        chatRoomId: callDetail?.caller
          ? currentChatRoomId
          : callDetail?.chatRoomId,
      });

      sendMessage({
        callType: mediaOptions.video ? "video" : "voice",
        callRejectReason: reason,
        sender: callDetail?.caller ? userId : callDetail?.callerId,
        chatRoomId: callDetail?.caller
          ? currentChatRoomId
          : callDetail?.chatRoomId,
      });

      cleanup();
    },
    [
      socketEmit,
      callDetail,
      currentChatRoomId,
      mediaOptions,
      sendMessage,
      userId,
      cleanup,
    ]
  );

  const endCall = useCallback(() => {
    socketEmit("user:endCall", {
      duration,
      chatRoomId: callDetail?.caller
        ? currentChatRoomId
        : callDetail?.chatRoomId,
    });

    sendMessage({
      callType: mediaOptions.video ? "video" : "voice",
      callDuration: duration,
      sender: callDetail?.caller ? userId : callDetail?.callerId,
      chatRoomId: callDetail?.caller
        ? currentChatRoomId
        : callDetail?.chatRoomId,
    });

    cleanup();
  }, [
    socketEmit,
    sendMessage,
    duration,
    callDetail,
    currentChatRoomId,
    mediaOptions,
    userId,
    cleanup,
  ]);

  return {
    userStream,
    userMediaRef,
    partnerMediaRef,
    callStatus,
    acceptCall,
    callAccepted,
    endCall,
    denyCall,
    duration,
  };
};

export default usePeer;
